/**
 * remark plugin: turns `[S<n>]` markers inside text into `cite` elements that the
 * Markdown renderer maps to <CitationChip>. Runs on the mdast, so tables, lists
 * and emphasis keep their structure; code and inline code are left untouched
 * (their content is a `value`, not text children). Markers inside links are
 * skipped so we never nest a button inside an anchor.
 */

type MdNode = {
  type: string
  value?: string
  children?: MdNode[]
  data?: Record<string, unknown>
}

const MARKER = /\[(S\d+)\]/g
const SKIP_PARENTS = new Set(['link', 'linkReference'])

function splitText(value: string): MdNode[] | null {
  MARKER.lastIndex = 0
  if (!MARKER.test(value)) return null
  MARKER.lastIndex = 0

  const nodes: MdNode[] = []
  let cursor = 0
  for (const match of value.matchAll(MARKER)) {
    const index = match.index ?? 0
    if (index > cursor) nodes.push({ type: 'text', value: value.slice(cursor, index) })
    const id = match[1] ?? ''
    nodes.push({
      type: 'citation',
      data: { hName: 'cite', hProperties: { dataSourceId: id } },
      children: [{ type: 'text', value: match[0] }],
    })
    cursor = index + match[0].length
  }
  if (cursor < value.length) nodes.push({ type: 'text', value: value.slice(cursor) })
  return nodes
}

function walk(node: MdNode): void {
  if (!node.children || SKIP_PARENTS.has(node.type)) return
  const next: MdNode[] = []
  let changed = false
  for (const child of node.children) {
    if (child.type === 'text' && typeof child.value === 'string') {
      const parts = splitText(child.value)
      if (parts) {
        next.push(...parts)
        changed = true
        continue
      }
    } else {
      walk(child)
    }
    next.push(child)
  }
  if (changed) node.children = next
}

export function remarkCitations() {
  return (tree: MdNode) => {
    walk(tree)
  }
}
